import { CardContainer, P2GStack, P2GText, P2GLink } from '@parcel2go/core-ui';
import { Parcel } from '../parcel';
import { ParcelSelector } from '../parcelSelector';
import TrackingNotifications from '../trackingnotifications';
import { useTranslation, Trans } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { CreateCaseNoteResponse, Tracking, TrackingEvent } from '~/lib/types/tracking-types';
import { Clipboard } from '~/components/tracking/clipboard';
import { useCallback, useEffect, useState } from 'react';

type TrackingHistoryProps = {
  tracking: Tracking;
  parcelNumbers: string[];
  withWindow: TrackingEvent | null;
  setOpenValidation: (value: boolean) => void;
  onSendCaseReply: (caseId: string, note: string) => Promise<CreateCaseNoteResponse>;
};

export default function TrackingHistory({
  parcelNumbers,
  tracking,
  withWindow,
  setOpenValidation,
  onSendCaseReply
}: TrackingHistoryProps) {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const [openTrackingNumber, setOpenTrackingNumber] = useState<string>('');

  const handleTabClick = useCallback(
    (value: string) => {
      if (openTrackingNumber !== value) {
        setOpenTrackingNumber(value);
      }
    },
    [openTrackingNumber]
  );

  useEffect(() => {
    if (parcelNumbers) {
      setOpenTrackingNumber(parcelNumbers[0]);
    }
  }, [parcelNumbers]);

  return (
    <CardContainer id="tracking-history">
      <P2GStack spacing={1}>
        <P2GStack direction="row" spacing={1} alignItems="baseline">
          <P2GText variant="h3" component="h3">
            <Trans t={t} i18nKey="HISTORY_TITLE">
              Tracking History
            </Trans>
          </P2GText>

          {!tracking.validatedOwnership && (
            <P2GText component="p">
              <P2GLink onClick={() => setOpenValidation(true)}>
                <Trans t={t} i18nKey="HISTORY_BUTTON_SHOW_MORE_DETAILS">
                  See more details
                </Trans>
              </P2GLink>
            </P2GText>
          )}
        </P2GStack>
        <P2GText variant="body1" component="p">
          <Trans
            t={t}
            i18nKey="HISTORY_SUBTITLE"
            count={parcelNumbers.length}
            values={{ count: parcelNumbers.length }}
            defaults="With this order you have {{count}} parcel(s)."
          />
        </P2GText>

        {tracking.hasMultipleParcels && (
          <ParcelSelector
            parcelNumbers={parcelNumbers}
            tracking={tracking}
            handleTabClick={handleTabClick}
            openTrackingNumber={openTrackingNumber!}
          />
        )}

        <TrackingNotifications
          tracking={tracking}
          onOpenValidation={() => setOpenValidation(true)}
          onSendReply={onSendCaseReply}
          withWindow={withWindow}
          parcelNumbers={parcelNumbers}
          isPerParcelNotification={true}
          currentParcel={tracking.parcels[openTrackingNumber || parcelNumbers[0]]}
        />
        {tracking.started && (
          <P2GText variant="body1" component="p">
            <Trans t={t} i18nKey="HISTORY_TRACKING_NUMBER">
              Courier tracking number: <strong>{openTrackingNumber}</strong>
            </Trans>{' '}
            <Clipboard value={openTrackingNumber}>
              <Trans t={t} i18nKey="HISTORY_CLIPBOARD">
                Copy Number
              </Trans>
            </Clipboard>
          </P2GText>
        )}

        <Parcel
          parcelNumber={openTrackingNumber || parcelNumbers[0]}
          service={tracking.order.service}
          parcel={tracking.parcels[openTrackingNumber || parcelNumbers[0]]}
          multiParcels={false}
          metadata={tracking.metadata}
          order={tracking.order}
        />
      </P2GStack>
    </CardContainer>
  );
}
